import React from 'react';
import { ShieldCheck, Lock, Apple } from 'lucide-react';

// Custom, simplified payment mark tiles \u2014 recognizable at a glance without
// hotlinking any third-party logo asset. Each tile is a 44x28 rounded rectangle
// with the brand's primary color / typography treatment.

const Tile = ({ children, bg = 'white', label, border = true }) => (
  <div
    aria-label={label}
    className={`h-8 w-12 rounded-md flex items-center justify-center flex-shrink-0 shadow-sm ${border ? 'border border-stone-200' : ''}`}
    style={{ backgroundColor: bg }}
  >
    {children}
  </div>
);

const Visa = () => (
  <Tile label="Visa">
    <span
      className="italic font-extrabold text-[10px] tracking-tight leading-none"
      style={{ color: '#1A1F71', fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}
    >VISA</span>
  </Tile>
);

const Mastercard = () => (
  <Tile label="Mastercard">
    <svg viewBox="0 0 40 24" width="34" height="20" aria-hidden="true">
      <circle cx="15" cy="12" r="8" fill="#EB001B" />
      <circle cx="25" cy="12" r="8" fill="#F79E1B" opacity="0.9" />
      <path d="M20 6 a8 8 0 0 0 0 12 a8 8 0 0 0 0 -12" fill="#FF5F00" />
    </svg>
  </Tile>
);

const Amex = () => (
  <Tile label="American Express" bg="#006FCF" border={false}>
    <span className="font-extrabold text-[8.5px] tracking-widest text-white leading-none" style={{ fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}>
      AMEX
    </span>
  </Tile>
);

const Discover = () => (
  <Tile label="Discover">
    <div className="flex items-center gap-1">
      <span className="font-extrabold text-[7px] tracking-wide text-stone-900 leading-none" style={{ fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}>
        DISC
      </span>
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF6000' }}></span>
    </div>
  </Tile>
);

const ApplePay = () => (
  <Tile label="Apple Pay" bg="#000000" border={false}>
    <div className="flex items-center gap-0.5">
      <Apple size={11} color="white" fill="white" strokeWidth={0} />
      <span className="text-white font-semibold text-[10px] leading-none" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>Pay</span>
    </div>
  </Tile>
);

const GooglePay = () => (
  <Tile label="Google Pay">
    <div className="flex items-baseline gap-[1px]">
      <span className="font-extrabold text-[10px] leading-none" style={{ color: '#4285F4', fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}>G</span>
      <span className="font-semibold text-[9px] text-stone-700 leading-none" style={{ fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}>Pay</span>
    </div>
  </Tile>
);

const StripeLink = () => (
  <Tile label="Link by Stripe" bg="#00D66F" border={false}>
    <span className="font-extrabold text-[9px] tracking-wide text-white leading-none" style={{ fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}>
      Link
    </span>
  </Tile>
);

const CashApp = () => (
  <Tile label="Cash App Pay" bg="#00D632" border={false}>
    <span className="font-extrabold text-[13px] text-white leading-none" style={{ fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}>
      $
    </span>
  </Tile>
);

const Klarna = () => (
  <Tile label="Klarna" bg="#FFA8CD" border={false}>
    <span className="font-extrabold text-[8px] tracking-tight leading-none" style={{ color: '#0A0A0A', fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}>
      Klarna
    </span>
  </Tile>
);

const TrustBadges = ({ compact = false }) => {
  const icons = [Visa, Mastercard, Amex, Discover, ApplePay, GooglePay, StripeLink, CashApp, Klarna];

  return (
    <div className={`mx-auto ${compact ? 'max-w-2xl' : 'max-w-3xl'} text-center`}>
      {/* Secure payment pill */}
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-stone-200 shadow-sm">
        <ShieldCheck size={16} className="text-emerald-600" strokeWidth={2.4} />
        <span className="text-[13px] font-semibold text-stone-800">
          Secure checkout powered by Stripe
        </span>
      </div>

      {/* Icon strip */}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {icons.map((I, i) => <I key={i} />)}
      </div>

      {/* Fine-print trust line */}
      <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-stone-500">
        <Lock size={11} className="text-stone-500" />
        <span>256-bit SSL · PCI-DSS compliant · Cancel anytime from your dashboard</span>
      </div>
      <p className="mt-2 text-[11px] text-stone-500">
        Your business data is never sold or shared with third parties for marketing.
      </p>
    </div>
  );
};

export default TrustBadges;
